import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { anonymousGlowRequest, HEADER, AppError } from '@/networks';
import { usePreferenceStore } from '@/stores/usePreferenceStore';

const text = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    .refine((v) => v.trim().length > 0);
const code = z.string().regex(/^[A-Z][A-Z0-9_]{0,49}$/);
const id = z.uuid().refine((v) => v === v.toLowerCase());
const unique = <T>(rows: T[]) => new Set(rows).size === rows.length;
const entrySchema = z.strictObject({
  id,
  version: z.number().int().positive().max(2147483647),
  categoryCode: code,
  categoryTitle: text(200),
  question: text(1000),
  answer: text(10000),
  publishedAt: z.iso.datetime({ offset: true }),
  languageFallback: z
    .array(z.enum(['question', 'answer', 'categoryTitle']))
    .max(3)
    .refine(unique),
});
const pageSchema = z
  .strictObject({
    count: z.number().int().nonnegative().safe(),
    rows: z.array(entrySchema).max(20),
    nextPage: z.number().int().min(2).max(100001).nullish(),
    isLast: z.boolean(),
  })
  .refine(
    (v) =>
      v.count >= v.rows.length &&
      unique(v.rows.map((r) => r.id)) &&
      (v.isLast ? v.nextPage == null : v.nextPage != null),
  );
const categoriesSchema = z
  .strictObject({
    count: z.number().int().nonnegative().max(500),
    rows: z
      .array(
        z.strictObject({
          code,
          title: text(200),
          languageFallback: z.array(z.literal('title')).max(1),
        }),
      )
      .max(500),
    nextPage: z.null().optional(),
    isLast: z.literal(true),
  })
  .refine(
    (v) => v.count === v.rows.length && unique(v.rows.map((r) => r.code)),
  );
const filterSchema = z.strictObject({
  pageNum: z.number().int().min(1).max(100000),
  pageSize: z.number().int().min(1).max(20),
  categoryCode: code.optional(),
  searchKeyword: z
    .string()
    .max(100)
    .refine((v) => !/[\p{Cc}\p{Cs}]/u.test(v))
    .optional(),
});
export type PublicFaq = z.infer<typeof entrySchema>;
export type PublicFaqFilters = z.infer<typeof filterSchema>;
const currentDisplay = () =>
  `${usePreferenceStore.getState().locale ?? 'en'}:${usePreferenceStore.getState().currency}`;
async function read(
  url: string,
  signal: AbortSignal,
  display: string,
  query?: Record<string, unknown>,
) {
  if (
    !/^(en|zh-CN):(KRW|USD|CNY)$/.test(display) ||
    display !== currentDisplay()
  )
    throw new Error('FAQ_DISPLAY_CHANGED');
  const [locale, currency] = display.split(':');
  const response = await anonymousGlowRequest<unknown>({
    method: 'get',
    url,
    queryParams: query,
    timeout: 15000,
    signal,
    headers: { [HEADER.LOCALE]: locale, [HEADER.CURRENCY]: currency },
  });
  if (signal.aborted || display !== currentDisplay())
    throw new Error('FAQ_DISPLAY_CHANGED');
  return response.data;
}
export async function readPublicFaqPage(
  filters: PublicFaqFilters,
  signal: AbortSignal,
  display = currentDisplay(),
) {
  try {
    const parsed = filterSchema.parse(filters);
    const input = {
      ...parsed,
      ...(parsed.searchKeyword !== undefined
        ? { searchKeyword: parsed.searchKeyword.normalize('NFKC').trim() }
        : {}),
    };
    filterSchema.parse(input);
    const page = pageSchema.parse(
      await read('/public/v1/faqs', signal, display, input),
    );
    const expected = Math.min(
      input.pageSize,
      Math.max(0, page.count - (input.pageNum - 1) * input.pageSize),
    );
    if (
      page.rows.length !== expected ||
      page.isLast !== input.pageNum * input.pageSize >= page.count ||
      (!page.isLast && page.nextPage !== input.pageNum + 1) ||
      (input.categoryCode &&
        page.rows.some((r) => r.categoryCode !== input.categoryCode))
    )
      throw new Error('FAQ_PAGE_INVALID');
    return page;
  } catch {
    throw new AppError(new Error('PUBLIC_FAQ_UNAVAILABLE'));
  }
}
export async function readPublicFaq(
  idValue: string,
  signal: AbortSignal,
  display = currentDisplay(),
) {
  try {
    id.parse(idValue);
    const row = z
      .strictObject({ row: entrySchema })
      .parse(await read(`/public/v1/faqs/${idValue}`, signal, display)).row;
    if (row.id !== idValue) throw new Error('FAQ_ID_INVALID');
    return row;
  } catch {
    throw new AppError(new Error('PUBLIC_FAQ_UNAVAILABLE'));
  }
}
export async function readPublicFaqCategories(
  signal: AbortSignal,
  display = currentDisplay(),
) {
  try {
    return categoriesSchema.parse(
      await read('/public/v1/faq-categories', signal, display),
    );
  } catch {
    throw new AppError(new Error('PUBLIC_FAQ_UNAVAILABLE'));
  }
}
const querySettings = {
  retry: false,
  staleTime: 0,
  gcTime: 0,
  refetchOnWindowFocus: false,
} as const;
export function usePublicFaqPage(
  filters: PublicFaqFilters,
  display: string,
  enabled: boolean,
) {
  return useQuery({
    ...querySettings,
    queryKey: ['actualFaq', 'page', display, filters],
    enabled,
    queryFn: ({ signal }) => readPublicFaqPage(filters, signal, display),
  });
}
export function usePublicFaqCategories(display: string, enabled: boolean) {
  return useQuery({
    ...querySettings,
    queryKey: ['actualFaq', 'categories', display],
    enabled,
    queryFn: ({ signal }) => readPublicFaqCategories(signal, display),
  });
}
export function usePublicFaq(
  idValue: string,
  display: string,
  enabled: boolean,
) {
  return useQuery({
    ...querySettings,
    queryKey: ['actualFaq', 'entry', display, idValue],
    enabled,
    queryFn: ({ signal }) => readPublicFaq(idValue, signal, display),
  });
}
